import React, { useState, useRef } from "react";
import { useQuery } from "react-query";
import { useForm } from "react-hook-form";
import { Form, Header, Segment, Comment, Button, Label, Icon, TextArea } from "semantic-ui-react";
import { getConversation, postComment } from "../../../queries/comments";
import ReactMarkdown from "react-markdown";
import Editor, { theme } from "@gogovapps/rich-markdown-editor";
import { trackCommunication } from "../../../mixpanel/communication";
import { CommentFileUploader } from "../../../components/CommentFileUploader";
import { SafeMoment } from "../../../components/SafeMoment";
import { ContactAvatar } from "./ContactAvatar";
import { formatBytes, getCamel } from "../../../utils/functions";

import "./CommunicationsCard.scss";
import useUserPermitContact from "../hooks/useUserPermitContact";
import identity from "../../../selectors/identity";
import { get } from "lodash";
import useApiCallWithToast from "../../../hooks/useApiCallWithToast";
import { PermitsContact } from "../types/PermitsContact";
import ContactConnectedProfilePopup from "../../contacts/components/ContactConnectedProfilePopup";
import { useIsOldPermitType } from "../hooks/useIsOldPermitType";
import usePermit from "../hooks/permits/usePermit";
import useNetworkHeaders from "../../../hooks/useNetworkHeaders";

type Comment = {
  authorId: number;
  conversationId: number;
  authorName: string;
  body: string;
  commentAttachment: any;
  createdAt: string;
};

type Participant = {
  participantId: number;
  conversationId: number;
  firstName: string;
  lastName: string;
  email: string;
};

interface ICommunicationsCardProps {
  site: string;
  permitsTypeName: string;
  permitNumber: string;
  permitId: string;
  isPublicView?: boolean;
}

export const CommunicationsCard = ({
  site,
  permitsTypeName,
  permitNumber,
  permitId,
  isPublicView = false,
}: ICommunicationsCardProps) => {
  const editorRef: any = useRef();
  const attachmentsForm = useForm();

  const networkHeaders = useNetworkHeaders();
  const isPermitAdmin = identity.isPermitsAdmin();
  const isPermitUser = identity.isPermitsUser();
  const userId = identity.getUserId() ?? "";

  const permitQuery = usePermit({ permitId: permitId });
  const contacts = getCamel<PermitsContact[]>(permitQuery.data, "data.attributes.permits-contacts", []);

  const oldPermitCreator = getCamel<{ firstName: string; lastName: string }>(
    permitQuery.data,
    "data.attributes.creator.data.attributes",
  );
  const oldPermitCreatorId = getCamel<number>(permitQuery.data, "data.attributes.creator-id");

  const { isOldPermitType, isLoading: isOldPermitTypeLoading } = useIsOldPermitType(permitId);

  const { contact: userContact, loading: userPermitContactLoading } = useUserPermitContact({ permitId });
  const oldPermitUserCanSendMessages = isOldPermitType && userId?.toString() === oldPermitCreatorId?.toString();
  const userCanSendMessages =
    userContact?.isApplicant ||
    userContact?.isSubmitter ||
    isPermitAdmin ||
    isPermitUser ||
    oldPermitUserCanSendMessages;

  const conversationQuery = useQuery(
    ["conversation", permitId.toString()],
    async () => await getConversation(networkHeaders)(permitId, "Permit"),
  );
  const comments = get<{ attributes: Comment }[]>(conversationQuery.data, "data.attributes.comments.data", []).map(
    (commentData) => commentData.attributes,
  );

  const participants = get<{ attributes: Participant }[]>(
    conversationQuery.data,
    "data.attributes.participants.data",
    [],
  ).filter((participantData) => {
    return participantData.attributes.participantId.toString() !== userId.toString();
  }); // remove current user

  const applicant = contacts.find((contact) => contact.isApplicant);

  const displayParticipants = contacts
    .filter((contact) => contact.isApplicant || (contact.isSubmitter && contact.userId !== applicant?.userId)) // only show applicant and submitter if they are separate users
    .map((contact) => ({
      contact,
      participant: participants.find(
        (participant) => participant.attributes.participantId.toString() === contact.userId?.toString(),
      )?.attributes,
    })) // map to participant
    .filter((contactAndParticipant) => !!contactAndParticipant.participant); // remove undefined participants

  //join in connected contact with access
  const connectedContacts = contacts
    .filter((contact) => contact.isActive && contact.userId !== null && contact.hasAccess)
    .filter((contact) => !contact.isApplicant && !contact.isSubmitter)
    .map((contact) => ({
      contact,
      participant: { firstName: contact.firstName, lastName: contact.lastName, email: contact.email },
    })); // map to participant
  const displayParticipantsWithConnectedContacts = [...displayParticipants, ...connectedContacts];

  const noParticipants = displayParticipants.length === 0;

  const { run: sendComment, loading: sendCommentLoading } = useApiCallWithToast({
    apiCall: () => postComment(networkHeaders),
    queryKey: ["conversation", permitId.toString()],
    shouldToastSuccess: false,
  });

  const loadingConversation = conversationQuery.isLoading || userPermitContactLoading || isOldPermitTypeLoading;

  const [comment, setComment] = useState("");
  const [attachmentData, setAttachmentData] = useState<
    Array<{ file_name: string; file_size: string; file_type: string; url: string }>
  >([]);

  const defaultAttachments = attachmentData.map((attachment: any) => {
    return { name: attachment?.file_name, signedUrl: attachment?.url };
  });
  const onUploadAttachment = (data: any) => {
    const attachment = {
      file_name: data.name,
      file_size: data.size,
      file_type: data.type,
      url: data.url,
    };
    setAttachmentData(() => [...attachmentData, attachment]);
  };

  const onRemoveAttachment = (data: any) => {
    const fileNameToRemove = data.name;
    const newAttachmentData = attachmentData.filter((attachment) => {
      return attachment?.file_name !== fileNameToRemove;
    });
    setAttachmentData(() => newAttachmentData);
  };

  const onAddComment = async () => {
    trackCommunication(site, permitsTypeName, permitNumber);
    await sendComment({
      "body": comment,
      "commentable-id": permitId,
      "commentable-type": "Permit",
      ...(attachmentData.length !== 0 && { comment_attachment_attributes: attachmentData }),
    });
    clearAttachment();
    clearEditor();
  };

  const clearAttachment = () => {
    setAttachmentData([]);
  };

  const clearEditor = () => {
    setComment("");

    if (isPublicView) {
      editorRef.current.focus();
    } else {
      // focus at the start of the editor
      // prevents error when text is selected and editor is cleared
      editorRef.current.focusAtStart();
      // clear editor
      editorRef.current.element.children[0].innerText = "";
    }
  };

  const participantFullName = (
    participant?: Omit<Participant, "participantId" | "conversationId"> &
      Partial<Pick<Participant, "participantId" | "conversationId">>,
  ) => {
    if (!participant) return "";
    return `${participant.firstName} ${participant.lastName}`;
  };

  const buildParticipantsTitle = () => {
    if (isOldPermitType && !isPublicView) {
      return `${oldPermitCreator?.firstName} ${oldPermitCreator?.lastName}`;
    }
    if (isPublicView) {
      return "Conversation with Permit Staff";
    }
    if (noParticipants) {
      return "There's no one here...";
    }
    if (displayParticipantsWithConnectedContacts.length === 1) {
      return (
        <ContactConnectedProfilePopup
          contact={displayParticipants[0].contact}
          trigger={<span className='contact-link'>{participantFullName(displayParticipants[0].participant)}</span>}
        />
      );
    }
    if (displayParticipantsWithConnectedContacts.length === 2) {
      return (
        <>
          <ContactConnectedProfilePopup
            contact={displayParticipantsWithConnectedContacts[0].contact}
            trigger={
              <span className='contact-link'>
                {participantFullName(displayParticipantsWithConnectedContacts[0].participant)}
              </span>
            }
          />
          &nbsp;and&nbsp;
          <ContactConnectedProfilePopup
            contact={displayParticipantsWithConnectedContacts[1].contact}
            trigger={
              <span className='contact-link'>
                {participantFullName(displayParticipantsWithConnectedContacts[1].participant)}
              </span>
            }
          />
        </>
      );
    }
    return (
      <>
        <ContactConnectedProfilePopup
          contact={displayParticipantsWithConnectedContacts[0].contact}
          trigger={
            <span className='contact-link'>
              {participantFullName(displayParticipantsWithConnectedContacts[0].participant)}
            </span>
          }
        />
        &nbsp;and {displayParticipantsWithConnectedContacts.length - 1} others
      </>
    );
  };

  const buildConversationFooter = () => {
    if (!userCanSendMessages) {
      return (
        <div className='footer-read-only-text'>
          Only the application submitter or the listed applicant can send messages
        </div>
      );
    }
    return (
      <Form reply>
        {isPublicView ? (
          <>
            <label className='label-text tw-mb-1 tw-block' htmlFor='new_comment_textarea'>
              Message
            </label>
            <TextArea
              ref={editorRef}
              className='tw-mb-2 tw-p-2'
              value={comment}
              onChange={(e, { value }: any) => setComment(value)}
              id='new_comment_textarea'
              required
            />
          </>
        ) : (
          <div id='new_comment' tabIndex={-1} role='textbox' className={"wysiwyg-editor-wrapper"}>
            <Editor
              readOnly={noParticipants}
              ref={editorRef}
              disabledToolbarItems={["mark"]}
              disabledBlockMenuItems={["container_notice", "table", "checkbox_list"]}
              style={{ padding: "1rem" }}
              placeholder={"Send a message..."}
              theme={theme}
              onChange={(getValue: any) => {
                setComment(getValue().replace(/\n\\/g, "\n").replace("\\\n", "").trim());
              }}
            />
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <CommentFileUploader
            name={"permits_attachments"}
            control={attachmentsForm.control}
            defaultValue={defaultAttachments}
            folder='permits'
            onUploaded={onUploadAttachment}
            onRemoved={onRemoveAttachment}
          />
          <Button
            type='submit'
            onClick={onAddComment}
            loading={sendCommentLoading}
            disabled={sendCommentLoading || (comment == "" && attachmentData.length === 0)}
            content='Send'
            aria-label='Send message'
            primary
          />
        </div>
      </Form>
    );
  };

  if (loadingConversation) return <Segment className='communications-card' />;

  return (
    <Segment className={"communications-card"} style={{ height: isPublicView ? "70vh" : "calc(100vh - 148px)" }}>
      <Header dividing className='tw-mb-0 tw-pb-0'>
        <h2 className='tw-my-[12px] tw-ml-[24px]'>
          <div className='tw-flex tw-h-[44px] tw-items-center'>{buildParticipantsTitle()}</div>
        </h2>
      </Header>

      <Segment
        basic
        style={{
          flexGrow: 1,
          overflowY: "auto",
          margin: 0,
          padding: 0,
          display: "flex",
          flexDirection: "column-reverse",
        }}
      >
        <Comment.Group className={"comment-group"} style={{ width: "100%" }}>
          {comments &&
            comments.map(({ authorName, createdAt, body, commentAttachment, authorId }, index) => {
              return (
                <Comment key={index} style={{ display: "flex" }}>
                  <Comment.Content>
                    <ContactAvatar
                      contact={contacts.find((contact) => contact.userId?.toString() === authorId.toString())}
                      fullName={authorName}
                      linkToConnectedCitizen
                      size='3rem'
                    />
                  </Comment.Content>

                  <Comment.Content style={{ paddingLeft: "1rem", paddingTop: "0.25rem" }}>
                    <Comment.Author as='a'>{authorName}</Comment.Author>
                    <Comment.Metadata>
                      <div>
                        <SafeMoment format='MMMM D, LT'>{createdAt}</SafeMoment>
                      </div>
                    </Comment.Metadata>
                    <div className='comment-body'>
                      <ReactMarkdown>{body}</ReactMarkdown>
                    </div>
                    {commentAttachment &&
                      commentAttachment.map((attachment: any) => {
                        return (
                          <div key={attachment.data.id} className='attachment'>
                            <a href={attachment.data.attributes.signedUrl}>
                              <Label basic>
                                <div>
                                  <Icon name='attach' />
                                  <span>{attachment.data.attributes.fileName}</span>
                                </div>
                                <span className='file-size'>({formatBytes(attachment.data.attributes.fileSize)})</span>
                              </Label>
                            </a>
                          </div>
                        );
                      })}
                  </Comment.Content>
                </Comment>
              );
            })}
        </Comment.Group>
      </Segment>

      <Segment
        basic
        style={{
          backgroundColor: "rgba(0, 0, 0, .03)",
          borderTop: "1px solid rgba(34, 36, 38, 0.15)",
          marginTop: "0",
          paddingLeft: isPublicView ? "1rem" : "3rem",
        }}
      >
        {buildConversationFooter()}
      </Segment>
    </Segment>
  );
};
